import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAppContext } from '@/providers';
import { useToast } from '@/components/ui/toast';
import { t } from '@/lib/i18n/messages';
import {
  getInitiativeEnablers,
  updateEnablerBudget,
  updateEnablerStatus,
} from '@/lib/api/initiatives';
import type { InitiativeEnabler } from '@/types/initiative';
import type { Locale } from '@/types/auth';

interface InitiativeEnablersSectionProps {
  initiativeId: string;
  locale: Locale;
  canManage?: boolean;
}

function StatusBadge({ status, locale }: { status: string; locale: Locale }) {
  const colors =
    status === 'accepted'
      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
      : status === 'declined'
      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
      : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';

  const label =
    status === 'accepted'
      ? locale === 'ar' ? 'مقبول' : 'Accepted'
      : status === 'declined'
      ? locale === 'ar' ? 'مرفوض' : 'Declined'
      : locale === 'ar' ? 'قيد الانتظار' : 'Pending';

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${colors}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

interface BudgetEditModalProps {
  initialBudget: number;
  locale: Locale;
  onSave: (budget: number) => Promise<void>;
  onClose: () => void;
}

function BudgetEditModal({ initialBudget, locale, onSave, onClose }: BudgetEditModalProps) {
  const isRTL = locale === 'ar';
  const [value, setValue] = useState(String(initialBudget));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = async () => {
    const num = parseFloat(value);
    if (isNaN(num) || num <= 0) return;
    setSaving(true);
    await onSave(num);
    setSaving(false);
  };

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        dir={isRTL ? 'rtl' : 'ltr'}
        className="relative w-full max-w-sm bg-card rounded-lg shadow-2xl p-6"
      >
        <h3 className="text-base font-bold text-foreground mb-4">
          {locale === 'ar' ? 'تعديل الميزانية' : 'Edit Budget'}
        </h3>
        <input
          type="number"
          min="0.01"
          step="0.01"
          dir="ltr"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full h-10 px-3 rounded-xl border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm mb-4"
        />
        <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-xl border border-border text-slate-700 text-sm font-medium hover:bg-muted transition-colors"
          >
            {t(locale, 'cancelAction')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 h-10 rounded-xl bg-primary text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition-colors"
          >
            {saving ? '...' : t(locale, 'saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export function InitiativeEnablersSection({
  initiativeId,
  locale,
  canManage = false,
}: InitiativeEnablersSectionProps) {
  const { locale: ctxLocale } = useAppContext();
  const effectiveLocale = locale ?? ctxLocale;
  const isRTL = effectiveLocale === 'ar';
  const { toast } = useToast();

  const [enablers, setEnablers] = useState<InitiativeEnabler[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingBudget, setEditingBudget] = useState<InitiativeEnabler | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchEnablers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getInitiativeEnablers(initiativeId);
      if (res.success && Array.isArray(res.data)) {
        setEnablers(res.data);
      } else {
        setError(res.message ?? 'Failed to load enablers');
        setEnablers([]);
      }
    } catch (_e) {
      setError('Failed to load enablers');
      setEnablers([]);
    } finally {
      setLoading(false);
    }
  }, [initiativeId]);

  useEffect(() => { fetchEnablers(); }, [fetchEnablers]);

  const handleUpdateStatus = async (enabler: InitiativeEnabler, status: 'accepted' | 'declined') => {
    setActionLoading(`${enabler.id}-${status}`);
    try {
      const res = await updateEnablerStatus(initiativeId, enabler.id, { status });
      if (res.success) {
        toast(
          status === 'accepted'
            ? (effectiveLocale === 'ar' ? 'تم القبول' : 'Accepted')
            : (effectiveLocale === 'ar' ? 'تم الرفض' : 'Declined'),
          'success'
        );
        await fetchEnablers();
      } else {
        toast(res.message ?? t(effectiveLocale, 'error'), 'error');
      }
    } catch {
      toast(t(effectiveLocale, 'error'), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveBudget = async (budget: number) => {
    if (!editingBudget) return;
    try {
      const res = await updateEnablerBudget(initiativeId, editingBudget.id, { budget });
      if (res.success) {
        toast(effectiveLocale === 'ar' ? 'تم تحديث الميزانية' : 'Budget updated', 'success');
        setEditingBudget(null);
        await fetchEnablers();
      } else {
        toast(res.message ?? t(effectiveLocale, 'error'), 'error');
      }
    } catch {
      toast(t(effectiveLocale, 'error'), 'error');
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-32 mb-4 animate-pulse" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className={`text-base font-semibold text-foreground mb-4 ${isRTL ? 'text-right' : ''}`}>
          {effectiveLocale === 'ar' ? 'المُمكِّنون' : 'Enablers'}
        </h3>

        {error ? (
          <div className="flex flex-col items-center py-10 text-muted-foreground">
            <span className="material-icons-outlined text-4xl mb-2 text-red-400">error_outline</span>
            <p className="text-sm text-red-500 mb-2">{error}</p>
            <button
              type="button"
              onClick={fetchEnablers}
              className="text-xs text-primary hover:underline"
            >
              {effectiveLocale === 'ar' ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        ) : enablers.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-muted-foreground">
            <span className="material-icons-outlined text-4xl mb-2">group_off</span>
            <p className="text-sm">{effectiveLocale === 'ar' ? 'لا يوجد مُمكِّنون بعد' : 'No enablers yet'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className={`px-4 py-3 font-medium text-foreground-secondary ${isRTL ? 'text-right' : 'text-left'}`}>
                    {effectiveLocale === 'ar' ? 'الاسم / البريد' : 'Name / Email'}
                  </th>
                  <th className={`px-4 py-3 font-medium text-foreground-secondary ${isRTL ? 'text-right' : 'text-left'}`}>
                    {effectiveLocale === 'ar' ? 'الميزانية' : 'Budget'}
                  </th>
                  <th className={`px-4 py-3 font-medium text-foreground-secondary ${isRTL ? 'text-right' : 'text-left'}`}>
                    {effectiveLocale === 'ar' ? 'الحالة' : 'Status'}
                  </th>
                  {canManage && (
                    <th className={`px-4 py-3 font-medium text-foreground-secondary ${isRTL ? 'text-right' : 'text-left'}`}>
                      {effectiveLocale === 'ar' ? 'الإجراءات' : 'Actions'}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {enablers.map((enabler, i) => {
                  const displayName = enabler.enabler?.name || enabler.enabler?.name_en || enabler.invite_email || 'Unknown';
                  return (
                    <tr
                      key={enabler.id}
                      className={`border-t border-border ${i % 2 === 0 ? '' : 'bg-slate-50/50/30'}`}
                    >
                      <td className="px-4 py-3">
                        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className="w-7 h-7 rounded-full bg-secondary/10 flex items-center justify-center text-secondary text-xs font-bold flex-shrink-0 overflow-hidden">
                            {enabler.enabler?.logo ? (
                              <img src={enabler.enabler.logo} alt={displayName} className="w-full h-full object-cover" />
                            ) : (
                              displayName.charAt(0).toUpperCase()
                            )}
                          </div>
                          <span className="text-slate-800 font-medium truncate max-w-[180px]">{displayName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="text-slate-700 font-semibold">
                            {(enabler.budget ?? 0).toLocaleString()} ﷼
                          </span>
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => setEditingBudget(enabler)}
                              className="text-slate-400 hover:text-primary transition-colors"
                              aria-label={effectiveLocale === 'ar' ? 'تعديل الميزانية' : 'Edit budget'}
                            >
                              <span className="material-icons-outlined text-sm">edit</span>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={enabler.status} locale={effectiveLocale} />
                      </td>
                      {canManage && (
                        <td className="px-4 py-3">
                          {enabler.status === 'pending' && (
                            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <button
                                type="button"
                                onClick={() => handleUpdateStatus(enabler, 'accepted')}
                                disabled={actionLoading !== null}
                                className="text-green-600 hover:text-green-700 disabled:opacity-40 transition-colors"
                                aria-label={effectiveLocale === 'ar' ? 'قبول' : 'Accept'}
                              >
                                <span className="material-icons-outlined text-xl">check_circle</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleUpdateStatus(enabler, 'declined')}
                                disabled={actionLoading !== null}
                                className="text-red-500 hover:text-red-600 disabled:opacity-40 transition-colors"
                                aria-label={effectiveLocale === 'ar' ? 'رفض' : 'Decline'}
                              >
                                <span className="material-icons-outlined text-xl">cancel</span>
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingBudget && (
        <BudgetEditModal
          initialBudget={editingBudget.budget}
          locale={effectiveLocale}
          onSave={handleSaveBudget}
          onClose={() => setEditingBudget(null)}
        />
      )}
    </>
  );
}
